if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mediaControls: this.querySelectorAll('[data-media-gallery-control]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.mediaControls.length > 0) {
          this.elements.mediaControls.forEach((control) => {
            control.addEventListener('click', this.onMediaControlClick.bind(this));
          });
        }

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.addEventListener('click', (event) => {
            const button = event.target.closest('button');
            if (!button) return;
            const targetItem = button.closest('[data-target]');
            if (!targetItem) return;
            this.setActiveMedia(targetItem.dataset.target, false);
          });
          this.elements.thumbnails.addEventListener('mouseover', (event) => {
            const targetItem = event.target.closest('[data-target]');
            if (!targetItem) return;
            this.preloadSlideImage(targetItem.dataset.target);
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
          this.initThumbnailScrollbar();
        }
      }

      disconnectedCallback() {
        if (this.thumbnailScrollbarTrack) {
          this.thumbnailScrollbarTrack.removeEventListener('pointerdown', this.onScrollbarPointerDown);
        }
        if (this.thumbnailScrollbarSlider) {
          this.thumbnailScrollbarSlider.removeEventListener('scroll', this.onThumbnailScrollbarScroll);
        }
        if (this.thumbnailScrollbarResizeObserver) {
          this.thumbnailScrollbarResizeObserver.disconnect();
        }
        window.removeEventListener('resize', this.updateThumbnailScrollbar);
        window.removeEventListener('pointermove', this.onScrollbarPointerMove);
        window.removeEventListener('pointerup', this.onScrollbarPointerUp);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, { scrollIntoView = true } = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        if (scrollIntoView) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);

        this.preloadAdjacentSlideImages(mediaId);
        this.triggerActiveSlideImageLoad(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      preloadSlideImage(mediaId) {
        const slide = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`);
        if (!slide) return;
        const img = slide.querySelector('.product__modal-opener--image img') || slide.querySelector('.product__media img');
        if (!img?.src) return;
        this._preloadedMediaIds = this._preloadedMediaIds || new Set();
        if (this._preloadedMediaIds.has(mediaId)) return;
        this._preloadedMediaIds.add(mediaId);
        const preload = new Image();
        preload.src = img.src;
      }

      preloadAdjacentSlideImages(activeMediaId) {
        const items = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        const index = items.findIndex((el) => el.dataset.mediaId === activeMediaId);
        if (index === -1) return;
        if (index > 0) this.preloadSlideImage(items[index - 1].dataset.mediaId);
        if (index < items.length - 1) this.preloadSlideImage(items[index + 1].dataset.mediaId);
      }

      triggerActiveSlideImageLoad(activeMedia) {
        const img = activeMedia?.querySelector('.product__modal-opener--image img') || activeMedia?.querySelector('.product__media img');
        if (!img) return;
        img.loading = 'eager';
        if (!img.complete) img.src = img.src;
      }

      onMediaControlClick(event) {
        event.preventDefault();
        if (!this.elements.viewer) return;

        const direction = event.currentTarget.dataset.mediaGalleryControl;
        if (!direction) return;

        if (!this.mql.matches) {
          const buttonName = direction === 'next' ? 'next' : 'previous';
          const sliderButton = this.elements.viewer.querySelector(`button[name="${buttonName}"]`);
          if (sliderButton) sliderButton.click();
          return;
        }

        if (this.elements.thumbnails) {
          const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
          if (thumbnailItems.length === 0) return;
          const activeThumbnailButton = this.elements.thumbnails.querySelector('button[aria-current="true"]');
          const activeThumbnailItem = activeThumbnailButton
            ? activeThumbnailButton.closest('[data-target]')
            : thumbnailItems[0];
          const currentIndex = thumbnailItems.indexOf(activeThumbnailItem);
          if (currentIndex === -1) return;
          const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
          if (nextIndex < 0 || nextIndex >= thumbnailItems.length) return;
          const nextButton = thumbnailItems[nextIndex].querySelector('button');
          if (!nextButton) return;
          nextButton.click();
          return;
        }

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (mediaItems.length === 0) return;
        const activeItem = this.elements.viewer.querySelector('.is-active') || mediaItems[0];
        const currentIndex = mediaItems.indexOf(activeItem);
        if (currentIndex === -1) return;
        const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0 || nextIndex >= mediaItems.length) return;
        const nextMediaId = mediaItems[nextIndex].dataset.mediaId;
        if (!nextMediaId) return;
        this.setActiveMedia(nextMediaId, false);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initThumbnailScrollbar() {
        /* 全画面幅・全デバイスでカスタムスクロールバーを表示 */
        if (this.thumbnailScrollbarTrack) return;
        if (!this.elements.thumbnails) return;

        this.thumbnailScrollbarSlider = this.elements.thumbnails.querySelector('[id^="Slider-"]');
        if (!this.thumbnailScrollbarSlider) return;

        this.thumbnailScrollbarTrack = document.createElement('div');
        this.thumbnailScrollbarTrack.className = 'custom-scrollbar';
        this.thumbnailScrollbarTrack.setAttribute('aria-hidden', 'true');

        this.thumbnailScrollbarThumb = document.createElement('div');
        this.thumbnailScrollbarThumb.className = 'custom-scrollbar__thumb';
        this.thumbnailScrollbarTrack.appendChild(this.thumbnailScrollbarThumb);
        this.elements.thumbnails.appendChild(this.thumbnailScrollbarTrack);

        this.onScrollbarPointerDown = this.onScrollbarPointerDown.bind(this);
        this.onScrollbarPointerMove = this.onScrollbarPointerMove.bind(this);
        this.onScrollbarPointerUp = this.onScrollbarPointerUp.bind(this);
        this.updateThumbnailScrollbar = this.updateThumbnailScrollbar.bind(this);
        this.onThumbnailScrollbarScroll = () => {
          if (this.thumbnailScrollbarRaf) return;
          this.thumbnailScrollbarRaf = requestAnimationFrame(() => {
            this.thumbnailScrollbarRaf = null;
            this.updateThumbnailScrollbar();
          });
        };

        this.thumbnailScrollbarSlider.addEventListener('scroll', this.onThumbnailScrollbarScroll);
        this.thumbnailScrollbarTrack.addEventListener('pointerdown', this.onScrollbarPointerDown);
        this.thumbnailScrollbarResizeObserver = new ResizeObserver(() => this.updateThumbnailScrollbar());
        this.thumbnailScrollbarResizeObserver.observe(this.thumbnailScrollbarSlider);
        window.addEventListener('resize', this.updateThumbnailScrollbar);
        this.updateThumbnailScrollbar();
      }

      isIOSMobile() {
        const isIOS =
          /iP(ad|hone|od)/.test(window.navigator.userAgent) ||
          (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
        return isIOS && window.matchMedia('(max-width: 749px)').matches;
      }

      updateThumbnailScrollbar() {
        if (!this.thumbnailScrollbarTrack || !this.thumbnailScrollbarThumb || !this.thumbnailScrollbarSlider) return;
        const scrollWidth = this.thumbnailScrollbarSlider.scrollWidth;
        const clientWidth = this.thumbnailScrollbarSlider.clientWidth;

        /* iPhoneでもスクロールバーを常時表示（スクロール不要時はサムを全幅で表示） */
        this.thumbnailScrollbarTrack.style.display = 'block';

        if (scrollWidth <= clientWidth) {
          const trackWidth = this.thumbnailScrollbarTrack.clientWidth;
          this.thumbnailScrollbarThumb.style.width = trackWidth ? `${trackWidth}px` : '100%';
          this.thumbnailScrollbarThumb.style.transform = 'translateX(0)';
          return;
        }

        const trackWidth = this.thumbnailScrollbarTrack.clientWidth;
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 20);
        const maxScroll = scrollWidth - clientWidth;
        const maxThumbX = trackWidth - thumbWidth;
        const thumbX = maxScroll > 0 ? (this.thumbnailScrollbarSlider.scrollLeft / maxScroll) * maxThumbX : 0;

        this.thumbnailScrollbarThumb.style.width = `${thumbWidth}px`;
        this.thumbnailScrollbarThumb.style.transform = `translateX(${thumbX}px)`;
      }

      onScrollbarPointerDown(event) {
        if (!this.thumbnailScrollbarTrack || !this.thumbnailScrollbarThumb || !this.thumbnailScrollbarSlider) return;
        event.preventDefault();
        this.thumbnailScrollbarTrack.setPointerCapture?.(event.pointerId);

        const trackRect = this.thumbnailScrollbarTrack.getBoundingClientRect();
        const trackWidth = this.thumbnailScrollbarTrack.clientWidth;
        const scrollWidth = this.thumbnailScrollbarSlider.scrollWidth;
        const clientWidth = this.thumbnailScrollbarSlider.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 20);
        const maxThumbX = Math.max(trackWidth - thumbWidth, 0);
        const pointerX = event.clientX - trackRect.left;
        const targetThumbX = Math.min(Math.max(pointerX - thumbWidth / 2, 0), maxThumbX);

        if (maxScroll > 0 && maxThumbX > 0) {
          this.thumbnailScrollbarSlider.scrollLeft = (targetThumbX / maxThumbX) * maxScroll;
        }

        this.isScrollbarDragging = true;
        this.scrollbarDragStartX = event.clientX;
        this.scrollbarDragStartScrollLeft = this.thumbnailScrollbarSlider.scrollLeft;

        window.addEventListener('pointermove', this.onScrollbarPointerMove);
        window.addEventListener('pointerup', this.onScrollbarPointerUp);
        this.updateThumbnailScrollbar();
      }

      onScrollbarPointerMove(event) {
        if (!this.isScrollbarDragging || !this.thumbnailScrollbarTrack || !this.thumbnailScrollbarSlider) return;
        const trackWidth = this.thumbnailScrollbarTrack.clientWidth;
        const scrollWidth = this.thumbnailScrollbarSlider.scrollWidth;
        const clientWidth = this.thumbnailScrollbarSlider.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 20);
        const maxThumbX = Math.max(trackWidth - thumbWidth, 0);
        const deltaX = event.clientX - this.scrollbarDragStartX;

        if (maxScroll > 0 && maxThumbX > 0) {
          const scrollDelta = (deltaX / maxThumbX) * maxScroll;
          this.thumbnailScrollbarSlider.scrollLeft = this.scrollbarDragStartScrollLeft + scrollDelta;
        }
      }

      onScrollbarPointerUp(event) {
        if (!this.isScrollbarDragging) return;
        this.isScrollbarDragging = false;
        this.thumbnailScrollbarTrack?.releasePointerCapture?.(event.pointerId);
        window.removeEventListener('pointermove', this.onScrollbarPointerMove);
        window.removeEventListener('pointerup', this.onScrollbarPointerUp);
      }
    }
  );
}
